import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { detectContactInfo } from '@/lib/validators';
import { translateFarmsError } from '../lib/translateError';
import type { ChatMessage } from '../types';

const CONTACT_BLOCK_MSG =
  'Por segurança, não é permitido enviar telefones, CEP ou números de contato no chat. ' +
  'Mantenha a negociação dentro da plataforma.';

export function useChat(threadId: string | null) {
  const { user }    = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending]   = useState(false);
  const [sendError, setSendError] = useState('');
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!threadId) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    supabase
      .from('chat_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          setLoadError(translateFarmsError(error.message));
          setMessages([]);
        } else {
          setMessages((data as unknown as ChatMessage[]) ?? []);
        }
        setLoading(false);
      });
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return;
    // Tópico único por instância (ChatPanel e MiniChat podem abrir a mesma thread)
    const ch = supabase
      .channel(`thread-${threadId}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `thread_id=eq.${threadId}` },
        (payload) => {
          setMessages((prev) => {
            const msg = payload.new as ChatMessage;
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
        }
      )
      .subscribe();
    channelRef.current = ch;
    return () => { void supabase.removeChannel(ch); };
  }, [threadId]);

  async function sendMessage(content: string): Promise<boolean> {
    if (!threadId || !user || !content.trim()) return false;
    if (detectContactInfo(content)) { setSendError(CONTACT_BLOCK_MSG); return false; }
    setSending(true);
    setSendError('');
    try {
      const { error } = await supabase.from('chat_messages').insert({
        thread_id:      threadId,
        sender_user_id: user.id,
        content:        content.trim(),
      } as never);
      if (error) throw new Error(translateFarmsError(error.message));
      return true;
    } catch (e: any) {
      setSendError(e.message ?? 'Erro ao enviar mensagem.');
      return false;
    } finally {
      setSending(false);
    }
  }

  async function markRead() {
    if (!threadId || !user) return;
    await supabase
      .from('chat_participants')
      .update({ last_read_at: new Date().toISOString() } as never)
      .eq('thread_id', threadId)
      .eq('user_id', user.id);
  }

  return { messages, loading, loadError, sending, sendError, sendMessage, markRead };
}

export async function getOrCreateThread(farmId: string, farmOwnerUserId: string): Promise<string> {
  const { data, error } = await supabase.rpc('get_or_create_chat_thread' as never, {
    p_farm_id:             farmId,
    p_farm_owner_user_id:  farmOwnerUserId,
  } as never);
  if (error) throw new Error(error.message);
  return data as string;
}
